import { Toast } from 'vant';
import { SystemType } from '@/types/env';
import { DeviceInfo, NetworkType } from '@/types/global';

/**
 * 获得系统类型
 *
 * @return {SystemType} 系统类型
 */
export const getSystemType = (): SystemType => {
  const u = navigator.userAgent;
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1;
  const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
  if (isAndroid) return SystemType.Android;
  if (isiOS) return SystemType.iOS;
  return SystemType.Unknown;
};

/**
 * 校验身份证年纪是否大于限制
 *
 * @param {string} [cardId=''] 身份证 ID
 * @param {number} [limit=18] 年龄限制
 * @return {boolean} 是否大于年龄限制
 */
const checkIdOlderThanNumber = (cardId = '', limit = 18) => {
  const birthYear = Number(cardId.substring(6, 10));
  const birthMonth = Number(cardId.substring(10, 12));
  const birthDay = Number(cardId.substring(12, 14));
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const age = year - birthYear;
  if (age > limit) {
    return true;
  }
  if (age < limit) {
    return false;
  }
  if (birthMonth > month) {
    return false;
  }
  if (birthMonth < month) {
    return true;
  }
  if (birthDay <= day) {
    return true;
  }
  return false;
};

const ID_NUMBER_REG = /(^\d{15}$)|(^\d{17}([0-9]|X|x)$)/;
export const checkIfIdNumberValid = (input: string) => ID_NUMBER_REG.test(input);
export const checkIfIdOlder18 = (input: string) => checkIdOlderThanNumber(input);

const CELL_PHONE_NUMBER_REG = /^1[0-9]{10}$/;
export const checkIfCellPhoneNumberValid = (input: string) => CELL_PHONE_NUMBER_REG.test(input);

/**
 * 执行规则校验
 *
 * @param {*} [originObj={}] 源数据对象
 * @param {string[]} [keys=[]] 检验字段
 * @param {Record<string, any[]>} rulesMap 规则表
 * @param {*} [{ defaultRequired = true }={}] 字段是否默认不为空
 * @returns {boolean} 数据是否满足规则校验
 */
export const executeValidationValid = (
  originObj: any = {},
  keys: string[] = [],
  rulesMap: Record<string, any[]>,
  { defaultRequired = true } = {},
) => keys.every((key) => {
  const rules = (rulesMap as any)[key];
  const value = originObj[key];
  if (!rules) return defaultRequired ? !!value : true;
  return rules.every((rule: any) => {
    if (rule.required) return !!value;
    const { validator } = rule;
    if (validator) return validator?.(value);
    return true;
  });
});

/**
 * 打开 URL
 *
 * @param {string} url URL
 */
export const openURL = (url: string) => {
  const ref: any = window.open();
  console.log('open URL: ', ref);
  if (!ref) {
    // 打开失败，已被拦截
    window.location.href = url;
  } else {
    // 打开成功，更改url
    ref.location.href = url;
  }
};

/**
 * 根据网络字符串获得网络类型
 *
 * @param {string} [netString=''] 网络字符串 wifi/2g/3g/4g/5g
 * @return {*}  {NetworkType} 网络类型
 */
export const getNetworkTypeByNetString = (netString = ''): NetworkType => {
  let validNetworkType: NetworkType = NetworkType.Unknown;
  if (netString) {
    if (netString === 'wifi') {
      validNetworkType = NetworkType.Wifi;
    } else if (/^\dg$/.test(netString)) {
      validNetworkType = Number(netString.match(/^(\d)g$/)?.[1] as string);
    }
  }
  return validNetworkType;
};

/**
 * 对比客户端版本a是否不小于b
 *
 * @param {a, b} a 要对比的版本，b 被对比的版本
 * @return {*}  {boolean}
 */
export const notLowerVersion = (a: string, b: string): boolean => {
  if (a === b) {
    return true;
  }
  const aVersionNums = a.split('.');
  const bVersionNums = b.split('.');
  const len = Math.min(aVersionNums.length, bVersionNums.length);

  for (let i = 0; i < len; i++) {
    if (parseInt(aVersionNums[i], 10) > parseInt(bVersionNums[i], 10)) {
      return true;
    }

    if (parseInt(aVersionNums[i], 10) < parseInt(bVersionNums[i], 10)) {
      return false;
    }
  }
  if (aVersionNums.length > bVersionNums.length) {
    return true;
  }
  if (aVersionNums.length < bVersionNums.length) {
    return false;
  }
  return false;
};

/**
 * 判断是不是安卓
 *
 * @param {device} global data里面的device
 * @return {*}  {boolean}
 */
export const isAn = (device: DeviceInfo) => ((device?.system || '').toLocaleLowerCase() !== 'ios');

/**
 * 完全拦截Dom的点击事件
 *
 * @param {string} 点击之后的提示
 * @return {*}  {boolean}
 */
export const preventClick = (el: HTMLElement, text: string) => {
  try {
    // 通过clone去掉点击事件
    const newEl = el.cloneNode(true);
    el.parentNode?.replaceChild(newEl, el);
    newEl.addEventListener('click', () => {
      Toast(text || '此功能暂未开放，敬请期待');
    });
  } catch (error) {}
};
